import { useCallback, useState, useEffect } from 'react';
import { WifiOff, Loader2, CheckCircle2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useISPStatus, PANEL_REFETCH_INTERVAL } from './hooks/useISPStatus';
import { ISPGroupCard } from './components/ISPGroupCard';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ISPStatusSheet({ open, onOpenChange }: Props) {
  const { data, isLoading, error, dataUpdatedAt, isFetching } = useISPStatus(open);
  const [progress, setProgress] = useState(100);

  // Update progress bar based on time since last update
  useEffect(() => {
    if (!open || !dataUpdatedAt) return;

    const updateProgress = () => {
      const elapsed = Date.now() - dataUpdatedAt;
      const remaining = Math.max(0, 100 - (elapsed / PANEL_REFETCH_INTERVAL) * 100);
      setProgress(remaining);
    };

    updateProgress();
    const interval = setInterval(updateProgress, 50);
    return () => clearInterval(interval);
  }, [open, dataUpdatedAt]);

  // Reset progress when fetching starts
  useEffect(() => {
    if (isFetching) {
      setProgress(100);
    }
  }, [isFetching]);

  // Close panel on click anywhere except client list items
  const handlePanelClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Don't close if clicking on client list item (ISPGroupCard)
    if (target.closest('[data-isp-client]')) {
      return;
    }
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[85vw] max-w-md p-0 flex flex-col"
        onClick={handlePanelClick}
      >
        <SheetHeader className="px-4 py-3 border-b shrink-0 relative">
          <SheetTitle className="flex items-center gap-2">
            <WifiOff className="h-5 w-5 text-destructive" />
            Проблемы с интернетом
            {data && data.totalDown > 0 && (
              <span className="text-destructive">({data.totalDown})</span>
            )}
          </SheetTitle>
          {/* Auto-refresh progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-muted">
            <div
              className="h-full bg-primary/50 transition-[width] duration-100"
              style={{ width: `${progress}%` }}
            />
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-destructive">
              <p className="text-sm">Ошибка загрузки</p>
            </div>
          ) : !data || data.totalDown === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-2" />
              <p className="text-sm font-medium">Все работает!</p>
              <p className="text-xs">Проблем с интернетом не обнаружено</p>
            </div>
          ) : (
            Object.entries(data.groupedByISP).map(([isp, clients]) => (
              <ISPGroupCard key={isp} isp={isp} clients={clients} />
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
